import { type JSX, type MouseEventHandler, useCallback } from 'react';

import { useTranslation } from '@cx-modules/i18n-provider';
import { kebabCase, titleCase } from '@cx-modules/utils';

import { useSelector } from '../../providers/StoreProvider';
import { type Bet } from '../../types';
import { categoryIdToCategoryMap, getTranslatedBetTitle } from '../../utils/labels';

import { BetRowLeftBetContent, BetRowLeftBetName, BetRowLeftBetNameLink } from './style';

export interface BetRowLeftProps {
  bet: Bet;
  marketId: number;
  marketName: string;
  showHistory?: boolean;
}

export const BetRowLeft = ({ bet, marketName, showHistory }: BetRowLeftProps): JSX.Element => {
  const { categoryGroupId, translateBetNames } = useSelector(state => ({
    categoryGroupId: state.categoryGroupId,
    translateBetNames: state.translateBetNames,
  }));
  const categoryNs = categoryIdToCategoryMap?.[categoryGroupId];
  const ns = ['bettype', categoryNs, 'label'];

  const { t } = useTranslation(ns);

  const url = '';

  const isWinMarket = marketName === 'Win Market';
  const showHistoryLocal = isWinMarket && showHistory;
  const betTitle = `${bet?.name} ${bet?.line?.name || ''}`.trim();
  const betHistoryUrl = `/${url}/bet-history/${kebabCase(`${betTitle}`)}`;
  const betHistoryLinkTitle = `View odds history for ${betTitle}`;

  const translatedBetTitle = titleCase(
    getTranslatedBetTitle({
      betName: bet?.name || '',
      lineName: bet?.line?.name || '',
      ns,
      t,
    }),
  );

  const handleClick: MouseEventHandler<HTMLAnchorElement> = useCallback(
    e => {
      e.preventDefault();
      const width = 1000;
      const height = 700;

      const popupWindow = window.open(
        betHistoryUrl,
        betHistoryLinkTitle
          // eslint-disable-next-line no-useless-escape
          .replace(/[:.\+\/\?\&\=\#\%\-\s]/g, '_')
          .toLowerCase(),
        `width=${width},height=${height},toolbar=no,location=no,directories=no,
        status=yes,menubar=no,scrollbars=yes,resizable=yes,copyhistory=no`,
      );

      if (popupWindow) {
        popupWindow.focus();
        return popupWindow;
      } else {
        return false;
      }
    },
    [betHistoryLinkTitle, betHistoryUrl],
  );

  return (
    <BetRowLeftBetContent>
      {showHistoryLocal ? (
        <BetRowLeftBetNameLink href={betHistoryUrl} onClick={handleClick} target="_blank" title={betHistoryLinkTitle}>
          {translateBetNames ? translatedBetTitle : betTitle}
        </BetRowLeftBetNameLink>
      ) : (
        <BetRowLeftBetName data-testid="grid-bet">
          {translateBetNames ? translatedBetTitle : betTitle}
        </BetRowLeftBetName>
      )}
    </BetRowLeftBetContent>
  );
};
